import React, { useState, useEffect } from 'react';

const NoAnswerClientsHistory = ({ record, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determinar la URL de la API basada en el entorno
  const API_URL = process.env.NODE_ENV === 'production' 
    ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
    : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

  useEffect(() => {
    fetchHistory();
  }, [record.id]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/no-answer-clients/${record.id}/history`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'contact_attempt':
        return '📞';
      case 'follow_up':
        return '🔄';
      case 'email_sent':
        return '📧';
      case 'meeting_scheduled':
        return '📅';
      case 'issue_found':
        return '⚠️';
      case 'status_changed':
        return '🔄';
      case 'comment_added':
        return '💬';
      case 'contact_successful':
        return '✅';
      default:
        return '📝';
    }
  };

  const getActionText = (actionType) => {
    switch (actionType) {
      case 'contact_attempt':
        return 'Intento de Contacto';
      case 'follow_up':
        return 'Seguimiento Realizado';
      case 'email_sent':
        return 'Email Enviado';
      case 'meeting_scheduled':
        return 'Reunión Programada';
      case 'issue_found':
        return 'Problema Encontrado';
      case 'status_changed':
        return 'Cambio de Estado';
      case 'comment_added':
        return 'Comentario Agregado';
      case 'contact_successful':
        return 'Contacto Exitoso';
      default:
        return actionType;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente':
        return 'text-yellow-600 bg-yellow-100';
      case 'en_proceso':
        return 'text-blue-600 bg-blue-100';
      case 'completado':
        return 'text-green-600 bg-green-100';
      case 'cancelado':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                📋 Historial de Cliente
              </h2>
              <p className="text-blue-100 text-sm">
                {record.nombre_cliente} - Historial de acciones y seguimiento
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Información del cliente */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Cliente:</span>
              <div className="text-gray-900">{record.nombre_cliente}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Célula:</span>
              <div className="text-gray-900">{record.celula || 'N/A'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Campaña:</span>
              <div className="text-gray-900">{record.campana || 'N/A'}</div>
            </div>
            <div>
              <span className="font-medium text-gray-700">Estado:</span>
              <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                {record.status}
              </div>
            </div>
          </div>
        </div>

        {/* Contenido del historial */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Sin historial disponible
              </h3>
              <p className="text-gray-600">
                No hay acciones registradas para este cliente.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Historial de Acciones ({history.length})
              </h3>
              
              <div className="space-y-4">
                {history.map((entry, index) => (
                  <div key={entry.id || index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-sm">{getActionIcon(entry.action_type)}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {getActionText(entry.action_type)}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {formatDate(entry.fecha_accion)}
                          </span>
                        </div>
                        
                        {entry.comment && (
                          <p className="text-sm text-gray-700 mb-2">
                            {entry.comment}
                          </p>
                        )}
                        
                        {entry.details && (
                          <p className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">Detalles:</span> {entry.details}
                          </p>
                        )}
                        
                        {(entry.old_status || entry.new_status) && (
                          <div className="flex items-center space-x-2 text-xs">
                            {entry.old_status && (
                              <span className={`px-2 py-1 rounded-full ${getStatusColor(entry.old_status)}`}>
                                {entry.old_status}
                              </span>
                            )}
                            {entry.old_status && entry.new_status && (
                              <span className="text-gray-400">→</span>
                            )}
                            {entry.new_status && (
                              <span className={`px-2 py-1 rounded-full ${getStatusColor(entry.new_status)}`}>
                                {entry.new_status}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {entry.user_name && (
                          <div className="mt-2 text-xs text-gray-500">
                            Por: {entry.user_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoAnswerClientsHistory;
